import { useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Avatar from "@mui/material/Avatar";
import {
  Home,
  Users,
  UsersRound,
  History,
  Building2,
  UserCog,
  Briefcase,
  Palette,
  LogOut,
  ClipboardCheck,
} from "lucide-react";
import { useSession, signOut } from "@/lib/auth-client";
import { useAuthContext } from "@/components/auth";
import { ImpersonationBanner } from "@/components/admin/ImpersonationBanner";
import { BottomNavigation } from "@/components/organisms/BottomNavigation";
import {
  LayoutRoot,
  SkipLink,
  Header,
  AccentBar,
  MainContainer,
  Sidebar,
  NavList,
  NavItem,
  NavDivider,
  NavSectionLabel,
  MainContent,
} from "./DashboardLayout.styles";

const baseNavItems = [
  { path: "/", label: "Dashboard", icon: Home },
  { path: "/contacts", label: "Contacts", icon: Users },
  { path: "/reports/history", label: "Report History", icon: History },
  { path: "/templates", label: "Templates", icon: Palette },
];

// Org admin items - visible to org admins and super admins
const orgAdminNavItems = [{ path: "/team", label: "Team", icon: UsersRound }];

// Super admin items - visible to super admins only
const superAdminNavItems = [
  { path: "/tenants", label: "Tenants", icon: Building2 },
  { path: "/users", label: "Users", icon: UserCog },
  { path: "/jobs", label: "Jobs", icon: Briefcase },
  { path: "/approvals", label: "Approvals", icon: ClipboardCheck },
];

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAdmin, isSuperAdmin } = useAuthContext();
  const { data: session } = useSession();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    await signOut();
    window.location.href = "/login";
  };

  return (
    <LayoutRoot>
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <ImpersonationBanner />
      <Header>
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ letterSpacing: "-0.5px" }}
        >
          AgentEdge
        </Typography>
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          aria-label="Open user menu"
        >
          <Avatar sx={{ width: 32, height: 32 }}>
            {session?.user?.name?.[0] || session?.user?.email?.[0] || "?"}
          </Avatar>
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              {session?.user?.email}
            </Typography>
          </MenuItem>
          <MenuItem onClick={handleSignOut}>
            <ListItemIcon>
              <LogOut size={18} />
            </ListItemIcon>
            Sign Out
          </MenuItem>
        </Menu>
      </Header>
      <AccentBar />

      <MainContainer>
        <Sidebar>
          <NavList>
            {baseNavItems.map(({ path, label, icon: Icon }) => (
              <NavItem
                key={path}
                active={isActive(path)}
                onClick={() => navigate(path)}
              >
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <Icon size={20} />
                </ListItemIcon>
                <ListItemText primary={label} />
              </NavItem>
            ))}
          </NavList>

          {/* Org admin section - Team management */}
          {isAdmin && (
            <NavDivider>
              <NavSectionLabel>Team</NavSectionLabel>
              <NavList sx={{ p: 0 }}>
                {orgAdminNavItems.map(({ path, label, icon: Icon }) => (
                  <NavItem
                    key={path}
                    active={isActive(path)}
                    onClick={() => navigate(path)}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </NavItem>
                ))}
              </NavList>
            </NavDivider>
          )}

          {/* Super admin section - System management */}
          {isSuperAdmin && (
            <NavDivider>
              <NavSectionLabel>System</NavSectionLabel>
              <NavList sx={{ p: 0 }}>
                {superAdminNavItems.map(({ path, label, icon: Icon }) => (
                  <NavItem
                    key={path}
                    active={isActive(path)}
                    onClick={() => navigate(path)}
                  >
                    <ListItemIcon sx={{ minWidth: "auto" }}>
                      <Icon size={20} />
                    </ListItemIcon>
                    <ListItemText primary={label} />
                  </NavItem>
                ))}
              </NavList>
            </NavDivider>
          )}
        </Sidebar>

        <MainContent as="main" id="main-content" tabIndex={-1}>
          <Outlet />
        </MainContent>
      </MainContainer>

      {/* Mobile bottom navigation - only renders on mobile */}
      <BottomNavigation />
    </LayoutRoot>
  );
}
